/**
 * AI Assistant Panel Component
 * Provides AI-powered document improvement features
 */

import React, { useState, useCallback } from 'react';
import {
  Wand2,
  FileText,
  Globe,
  Shield,
  Plus,
  MessageSquare,
  X,
  Loader,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Sparkles,
  Scale,
  Languages,
  Minimize2
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '../../common/Card';
import { Button } from '../../common/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { documentAIService } from '../../../services/api/documentGeneratorService';
import {
  GeneratedDocument,
  AIImprovementMode,
  SUPPORTED_JURISDICTIONS
} from '../../../types/documentGenerator.types';

interface AIAssistantPanelProps {
  document: GeneratedDocument;
  content: string;
  onImprove: (improvedContent: string) => void;
  onClose: () => void;
}

interface AIMode {
  id: AIImprovementMode;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const aiModes: AIMode[] = [
  {
    id: 'professional',
    label: 'Professional',
    description: 'Enhance writing quality and tone',
    icon: <Wand2 className="w-5 h-5" />,
    color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
  },
  {
    id: 'simplified',
    label: 'Simplify',
    description: 'Plain language rewrite',
    icon: <FileText className="w-5 h-5" />,
    color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
  },
  {
    id: 'formal',
    label: 'Formal',
    description: 'More formal and legal tone',
    icon: <Scale className="w-5 h-5" />,
    color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
  },
  {
    id: 'concise',
    label: 'Concise',
    description: 'Shorter and more direct',
    icon: <Minimize2 className="w-5 h-5" />,
    color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
  },
  {
    id: 'elaborate',
    label: 'Elaborate',
    description: 'Add more detail and context',
    icon: <Plus className="w-5 h-5" />,
    color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
  },
  {
    id: 'legal',
    label: 'Legal',
    description: 'Legal terminology and precision',
    icon: <Shield className="w-5 h-5" />,
    color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
  }
];

export const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  document,
  content,
  onImprove,
  onClose
}) => {
  const { token } = useAuth();

  // State
  const [selectedMode, setSelectedMode] = useState<AIImprovementMode | null>(null);
  const [jurisdiction, setJurisdiction] = useState(document.jurisdiction || 'US-DE');
  const [customInstructions, setCustomInstructions] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    improved: string;
    changes: string[];
    confidence: number;
  } | null>(null);
  const [showJurisdictionSelect, setShowJurisdictionSelect] = useState(false);

  // Process AI improvement
  const handleProcess = useCallback(async () => {
    if (!token || !selectedMode) return;

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      const context = customInstructions || `Document: ${document.title}, Category: ${document.category}`;
      const aiResult = await documentAIService.improveContent(
        token,
        content,
        selectedMode,
        context
      );

      setResult({
        improved: aiResult.improved,
        changes: aiResult.changes || [],
        confidence: aiResult.confidence || 0.85
      });
    } catch (err: any) {
      setError(err.message || 'AI processing failed');
    } finally {
      setProcessing(false);
    }
  }, [token, selectedMode, content, document.title, document.category, customInstructions]);

  // Accept the improved content
  const handleAccept = useCallback(() => {
    if (result?.improved) {
      onImprove(result.improved);
      setResult(null);
      setSelectedMode(null);
      setCustomInstructions('');
    }
  }, [result, onImprove]);

  // Reset
  const handleReset = useCallback(() => {
    setResult(null);
    setSelectedMode(null);
    setCustomInstructions('');
    setError(null);
  }, []);

  return (
    <div className="w-96 flex-shrink-0">
      <Card className="sticky top-4 max-h-[calc(100vh-8rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <span className="font-semibold text-text-primary">AI Assistant</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <CardBody className="flex-1 overflow-auto space-y-6">
          {/* Mode Selection */}
          {!result && (
            <>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Select Improvement Mode
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {aiModes.map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => setSelectedMode(mode.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        selectedMode === mode.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${mode.color}`}>
                        {mode.icon}
                      </div>
                      <p className="font-medium text-sm text-text-primary">{mode.label}</p>
                      <p className="text-xs text-text-tertiary">{mode.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Jurisdiction Select (for localize mode) */}
              {selectedMode === 'legal' && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Target Jurisdiction
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => setShowJurisdictionSelect(!showJurisdictionSelect)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary"
                    >
                      <span className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        {SUPPORTED_JURISDICTIONS.find(j => j.code === jurisdiction)?.name || jurisdiction}
                      </span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {showJurisdictionSelect && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-surface-base border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-auto z-50">
                        {SUPPORTED_JURISDICTIONS.map((j) => (
                          <button
                            key={j.code}
                            onClick={() => {
                              setJurisdiction(j.code);
                              setShowJurisdictionSelect(false);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                              jurisdiction === j.code ? 'bg-gray-50 dark:bg-gray-800 font-medium' : ''
                            }`}
                          >
                            {j.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Custom Instructions */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Additional Instructions (Optional)
                </label>
                <textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm"
                  placeholder="Any specific instructions for the AI..."
                />
              </div>

              {/* Process Button */}
              <Button
                onClick={handleProcess}
                disabled={!selectedMode || processing}
                className="w-full gap-2"
              >
                {processing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    Improve Document
                  </>
                )}
              </Button>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>{error}</p>
                </div>
              )}
            </>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-4">
              {/* Confidence */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-sm text-text-secondary">Confidence Score</span>
                <span className="text-sm font-medium text-text-primary">
                  {Math.round(result.confidence * 100)}%
                </span>
              </div>

              {/* Changes Made */}
              {result.changes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-text-primary mb-2">Changes Made</h4>
                  <ul className="space-y-2">
                    {result.changes.slice(0, 5).map((change, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-text-secondary">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{change}</span>
                      </li>
                    ))}
                    {result.changes.length > 5 && (
                      <li className="text-xs text-text-tertiary pl-6">
                        +{result.changes.length - 5} more changes
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Preview */}
              <div>
                <h4 className="text-sm font-medium text-text-primary mb-2">Preview</h4>
                <div className="max-h-60 overflow-auto p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-text-secondary whitespace-pre-wrap line-clamp-[20]">
                    {result.improved.slice(0, 1000)}
                    {result.improved.length > 1000 && '...'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="secondary" onClick={handleReset} className="flex-1">
                  Try Again
                </Button>
                <Button onClick={handleAccept} className="flex-1 gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Accept Changes
                </Button>
              </div>
            </div>
          )}

          {/* Tips */}
          {!processing && !result && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                Tips for Better Results
              </h4>
              <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
                <li>• Select text to improve specific sections</li>
                <li>• Use "Professional" for general enhancement</li>
                <li>• Use "Simplify" for clearer language</li>
                <li>• Use "Legal" for precise legal terminology</li>
              </ul>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default AIAssistantPanel;
